import {
  Button,
  Grid,
  Menu,
  MenuItem,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import moment from 'moment';
import React, { FC, Fragment, useEffect, useState } from 'react';
import PGEButton from '../../../buttons';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { useTranslation } from '../../../../hooks/useTranslation';

import colors from '../../../../themes/main-colors';
import { PictureAsPdf } from '@material-ui/icons';
import PaperBox from '../../../utility/paper-box';
import {
  MAX_NUM_MONTHS_FOR_SHORT_TERM,
  TpaAccountDetails,
  TpaMonthsMatrix,
} from '../useTPA';
import SingleInstallment from '../confirm-plan/SingleInstallment';
import {
  getSelectedYearTPAMonthMatrix,
  getAllTpaPaymentsOverDue,
  getTpaPaymentsOverDueYearly,
  getLastIsPaymentCompleted,
  getUnpaidMonthFormLastIsPaymentCompleted,
} from '../utils';
import { formatDate, toDateString } from '../../../../util/format';
import useAuthQuery from '../../../../hooks/useAuthQuery';
import { getAccountDetailsBillDetailsQuery } from '../../query';
import { AccountDetail } from '../../../../__generated__/pge-types';
import useSelectedAccountParams from '../../../../hooks/useSelectedAccountParams';
import useDownloadBill from '../../../../hooks/useDownloadBill';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import TPAPaymentYear from '../../landing/tpa-view/TPAPaymentYear';
import { isEqual } from 'lodash';
import { navigate } from '@reach/router';
import routes from '../../../../routes';
import { convertToHigherValue } from '../tpaAmountCalculationUtil';
interface Props {
  tpaDetails: TpaAccountDetails | undefined;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paperCard: {
      boxShadow: 'inset 0px 2px 0px #27788C, 0px 3px 6px #00000029',
      padding: '0px !important',
    },
    leftSideAmount: {
      background: colors.white,
      rowGap: '20px',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
    },
    columnView: {
      padding: theme.spacing(2),
    },
    leftColumn: {
      backgroundColor: colors.lightBlue,
      borderRadius: '5px 0px 0px 5px',
      marginTop: '2px',
    },
    overDueBanner: {
      border: '1px solid red',
      borderRadius: '5px',
      backgroundColor: colors.white,
      color: colors.darkerRed,
      textAlign: 'center',
    },
    tpaPlanViewWrap: {
      padding: theme.spacing(2, 0, 0, 0),
    },
    completePlanInstallmentWrap: {
      textAlign: 'center',
      '& h5': {
        fontWeight: 300,
      },
      [theme.breakpoints.down('sm')]: {
        margin: '0 -16px', //-16 to nullify the padding
      },
    },
  }),
);
const PaymentPlanProgress: FC<Props> = ({ tpaDetails }) => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const { accountParams } = useSelectedAccountParams();
  const { data: getAccountDetailsData } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getAccountDetailsBillDetailsQuery, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams,
  });

  const tpaMatrix = tpaDetails?.enrolledInstallmentDetails?.monthsMatrix ?? [];

  const isExtraLongTPAPlan =
    tpaMatrix && tpaMatrix.length > MAX_NUM_MONTHS_FOR_SHORT_TERM;
  const [tpaPaymentSelectedYear, setTpaPaymentSelectedYear] = useState<string>(
    new Date().getFullYear().toString(),
  );
  const [relevantTPAMatrix, setRelevantTPAMatrix] = useState<TpaMonthsMatrix[]>(
    isExtraLongTPAPlan
      ? getSelectedYearTPAMonthMatrix(tpaPaymentSelectedYear, tpaMatrix)
      : tpaMatrix,
  );

  const [lastPayment, setLastPayment] = useState<TpaMonthsMatrix[]>(
    tpaMatrix ? getLastIsPaymentCompleted(tpaMatrix) : [],
  );

  const [lastUnpaidMonth, setLastUnpaidMonth] = useState<TpaMonthsMatrix[]>(
    relevantTPAMatrix
      ? getUnpaidMonthFormLastIsPaymentCompleted(relevantTPAMatrix)
      : [],
  );

  const tpaPaymentsOverdue: TpaMonthsMatrix[] = relevantTPAMatrix
    ? getTpaPaymentsOverDueYearly(tpaMatrix).filter(
        item => !item.isPaymentCompleted,
      )
    : [];

  const tpaPaymentsOverdueCurrentYear: TpaMonthsMatrix[] = tpaMatrix
    ? getTpaPaymentsOverDueYearly(tpaMatrix).filter(
        item => !item.isPaymentCompleted,
      )
    : [];

  if (!relevantTPAMatrix) {
    return null;
  }
  const tpaYears = [
    ...new Set(
      tpaMatrix?.map(item => item.paymentDate.getFullYear().toString()),
    ),
  ];

  useEffect(() => {
    if (tpaMatrix.length > 0) {
      setLastPayment(getLastIsPaymentCompleted(tpaMatrix));
    }
    if (tpaMatrix.length > 0) {
      setLastUnpaidMonth(
        getUnpaidMonthFormLastIsPaymentCompleted(tpaMatrix),
      );
    }
  }, [relevantTPAMatrix]);

  useEffect(() => {
    if (tpaYears.length > 0) {
      setRelevantTPAMatrix(
        getSelectedYearTPAMonthMatrix(tpaPaymentSelectedYear, tpaMatrix),
      );
    }
  }, [tpaPaymentSelectedYear]);

  useEffect(() => {
    if (
      tpaYears.length > 0 &&
      tpaYears.length === 1 &&
      tpaYears[0] != tpaPaymentSelectedYear
    ) {
      setTpaPaymentSelectedYear(tpaYears[0]);
    }
  }, [tpaYears]);

  const theme = useTheme();
  const downloader = useDownloadBill();
  const { wrapWithLoader } = useWrapWithLoader();
  const downloadBill = wrapWithLoader(
    async (e: React.MouseEvent, isSummary: boolean) => {
      if (e) {
        e.preventDefault();
        setAnchorEl(null);
      }

      const downloadLink = document.createElement('a');

      downloadLink.href = await downloader.downloadBill(
        getAccountDetailsData?.getAccountDetails?.[0].billInfo?.billDetails
          ?.encryptedBillId,
        isSummary,
      );

      downloadLink.download = `${t('VIEW_BILL').replace(
        ' ',
        '_',
      )}-${toDateString(
        getAccountDetailsData?.getAccountDetails?.[0].billInfo?.billDetails
          ?.billingPeriodStartDate,
      )}_${toDateString(
        getAccountDetailsData?.getAccountDetails?.[0].billInfo?.billDetails
          ?.billingPeriodEndDate,
      )}.pdf`;

      downloadLink.click();
    },
    () => t('NO_BILL_FOUND'),
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const getDate = (date: any = '') => {
    let fetchDate = new Date();
    if (date) {
      fetchDate = new Date(date);
    }
    fetchDate.setHours(0, 0, 0, 0);
    return fetchDate;
  };

  const getIsOverDueAmount = (amountData: TpaMonthsMatrix[]) => {
    const result = [];
    for (let i = amountData.length - 1; i >= 0; i--) {
      if (result.length === 0) {
        result.push(amountData[i]);
      } else {
        const lastMonth = result[result.length - 1].monthNumber;
        if (amountData[i].monthNumber === lastMonth - 1) {
          result.push(amountData[i]);
        } else {
          break; // Order breaks
        }
      }
    }
    return convertToHigherValue(
      result
        .map(tpaDue => tpaDue.monthlyAmount)
        .reduce((prev, curr) => prev + curr, 0),
      2,
    );
  };
 return (
    <Fragment>
      {tpaPaymentsOverdue &&
      tpaPaymentsOverdue.length > 0 &&
       (lastPayment &&
        lastPayment.length > 0 ?
        moment(lastPayment[lastPayment.length - 1].paymentDate,'day').isBefore 
        (
        moment(
          tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].paymentDate
        ).isBefore(moment(), 'day')
          ? moment(
            tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].paymentDate
          ,'day')
          : moment(
            tpaPaymentsOverdue[tpaPaymentsOverdue.length - 2].paymentDate
          ,'day')
        ) : (
        moment(
          tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].paymentDate
        ).isBefore(moment(), 'day')
          ? moment(
            tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].paymentDate
          ,'day')
          : moment(
            tpaPaymentsOverdue[tpaPaymentsOverdue.length - 2].paymentDate
          ,'day')
        )) && (
        <Typography variant="body2" className={classes.overDueBanner}>
        {richT('OVERDUE_BANNER_MESSAGE', {
          dueDate: formatDate(
          moment(
            tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].paymentDate
          ).isBefore(moment(), 'day')
            ? tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1]?.paymentDate
            : tpaPaymentsOverdue[tpaPaymentsOverdue.length - 2]?.paymentDate,
          'MM/dd/yy',
          ),
        })}
        </Typography>
      )}
      <PaperBox className={classes.paperCard}>
      <Grid container>
        <Grid
        item
        container
        sm={6}
        className={`${classes.columnView} ${classes.leftColumn}`}
        >
        <Grid item container style={{ textAlign: 'center' }}>
          <Grid item xs={12}>
          <Typography variant="body2">
            {richT('PAYMENT_PLAN_PROGRESS_HEADING_TEXT', {
            ACCOUNT_NUMBER: accountParams?.accountNumber,
            MONTHS: tpaDetails?.enrolledInstallmentDetails?.totalMonths!.toString(),
            END_DATE: formatDate(
              new Date(
              tpaDetails?.enrolledInstallmentDetails?.monthsMatrix[
                tpaDetails?.enrolledInstallmentDetails?.totalMonths -
                1
              ].paymentDate!,
              ),
              'MMM. yyyy',
            ),
            })}
          </Typography>

          <Grid
            item
            xs={12}
            container
            direction="column"
            className={classes.leftSideAmount}
          >
            <Grid item>
            <Typography variant="body2">
              {t('PAYMENT_PLAN_BALANCE')}
            </Typography>
            </Grid>
            <Grid item>
            <Typography variant="h1">
              $
              {tpaDetails?.enrolledInstallmentDetails?.remainingBalance.toFixed(
              2,
              )}
            </Typography>
            </Grid>
            <Grid item>
            <Typography
              variant="body2"
              style={{ color: colors.errorRed }}
            >
              {t('PAYMENT_PLAN_LATE_CHARGE_MESSAGE')}
            </Typography>
            </Grid>
          </Grid>

          {lastPayment &&
          lastPayment?.length &&
          lastPayment?.length > 0 ? (
            <Typography variant="body2">
            {richT('LAST_PAYMENT_TEXT', {
              monthlyAmount: lastPayment[
              lastPayment?.length - 1
              ]?.monthlyAmount.toString(),
              paymentDate: moment(
              lastPayment[lastPayment?.length - 1]?.paymentDate,
              ).format('MMM DD, YYYY'),
            })}
            </Typography>
          ) : null}
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ alignSelf: 'flex-end' }}>
          {tpaPaymentsOverdue && tpaPaymentsOverdue.length > 0 && (
          <Button
            variant="outlined"
            style={{
            whiteSpace: 'nowrap',
            background: colors.white,
            color: colors.sparkBlue,
            width: '100%',
            borderRadius: '25px',
            marginBottom: theme.spacing(2),
            }}
            size={'large'}
            onClick={async () => await navigate(routes.PAYMENT)}
            data-testid="paybill-button"
            id="paybill-button"
          >
            {t('PAY_BILL')}
          </Button>
          )}
          <PGEButton
          style={{ width: '100%', borderRadius: '25px' }}
          size={'large'}
          endIcon={<ExpandMore />}
          onClick={handleClick}
          data-testid="positioned-button"
          id="positioned-button"
          aria-controls={open ? 'positioned-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          >
          {t('DOWNLOAD_PDF')}
          </PGEButton>
          <Menu
          id="positioned-menu"
          aria-labelledby="positioned-button"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            style: {
            background: colors.brightBlue,
            color: colors.white,
            borderRadius: '25px',
            },
          }}
          >
          <MenuItem onClick={e => downloadBill(e, false)}>
            <PictureAsPdf style={{ marginRight: '10px' }} />
            {t('DETAILED_BILL_DOWNLOAD')}
          </MenuItem>
          {getAccountDetailsData?.getAccountDetails?.[0]
            .isSummaryBillAccount && (
            <MenuItem onClick={e => downloadBill(e, true)}>
            <PictureAsPdf style={{ marginRight: '10px' }} />
            {t('SUMMARY_BILL_DOWNLOAD')}
            </MenuItem>
          )}
          </Menu>
        </Grid>
        </Grid>
        <Grid item sm={6} style={{ width: '100%' }}>
        <Grid container className={classes.columnView}>
          {tpaYears && tpaYears.length && tpaYears.length > 1 ? (
          <Grid item xs={12}>
            <TPAPaymentYear
            tpaPaymentYears={tpaYears}
            handleTPAPaymentYearClick={year =>
              setTpaPaymentSelectedYear(year)
            }
            tpaPaymentSelectedYear={tpaPaymentSelectedYear}
            />
          </Grid>
          ) : null}
          <Grid item container xs={12}>
          {relevantTPAMatrix &&
            relevantTPAMatrix.map(
            (tpaInsMatrix: TpaMonthsMatrix, index: number) => {
              const checkIsAnyFuturePaymentCompleted = tpaMatrix.some(
                entry =>
                  entry.paymentDate > tpaInsMatrix?.paymentDate &&
                  entry.isPaymentCompleted,
              );
              return tpaInsMatrix.paymentDate && (
                <Grid item>
                <SingleInstallment
                  key={index}
                  isAnyFuturePaymentCompleted={checkIsAnyFuturePaymentCompleted}
                  installmentAmount={tpaInsMatrix.monthlyAmount}
                  installmentDate={tpaInsMatrix.paymentDate}
                  isFirstInstallment={false}
                  isPaymentCompleted={tpaInsMatrix?.isPaymentCompleted ||
                  checkIsAnyFuturePaymentCompleted}
                  isUpcomingPayment={(((tpaPaymentsOverdue && tpaPaymentsOverdue.length > 0 && lastPayment && 
                  lastPayment.length > 0
                    ? moment(lastPayment[lastPayment.length - 1].paymentDate).startOf('day').isBefore
                    (moment(tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].paymentDate).startOf('day'))
                    : true)) &&
                  (tpaPaymentsOverdue && tpaPaymentsOverdue.length > 0 && tpaInsMatrix.monthNumber ===
                    tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].monthNumber && moment(tpaInsMatrix.paymentDate).startOf('day').isSameOrAfter(moment().startOf('day')))
          ) || (tpaPaymentsOverdue && tpaPaymentsOverdue.length > 0 &&  
                    tpaInsMatrix.monthNumber ===
                    tpaPaymentsOverdue[tpaPaymentsOverdue.length - 1].monthNumber && moment(tpaInsMatrix.paymentDate).startOf('day').isSameOrAfter(moment().startOf('day')))
                  }
                  isTPAPaymentOverDue={tpaPaymentsOverdue.some(obj =>
                  isEqual(obj, tpaInsMatrix),
                  )}
                  isPastDate={
                  moment(tpaInsMatrix.paymentDate).startOf('day').isBefore(moment().startOf('day'))
                  }
                  overDueAmount={getIsOverDueAmount(
                  tpaPaymentsOverdueCurrentYear,
                  )}
                  isTPALandingPage={true}
                  isMonthOfInstallment={
                  tpaInsMatrix.monthNumber ===
                  (tpaMatrix &&
                    tpaMatrix.length > 0 &&
                    tpaMatrix[0].monthNumber)
                  }
                  withDownPaymentAmount={
                  moment(tpaInsMatrix.paymentDate).startOf('day').isBefore(moment().startOf('day')) &&
                  tpaInsMatrix.monthNumber ===
                    (tpaMatrix &&
                    tpaMatrix.length > 1 &&
                    tpaMatrix[1].monthNumber) &&
                  !tpaMatrix[0].isPaymentCompleted &&
                  tpaInsMatrix?.monthlyAmount +
                    relevantTPAMatrix[index - 1]?.monthlyAmount
                  }
                  isRollUp={
                  lastUnpaidMonth?.length > 0
                    ? tpaPaymentsOverdue.length > 0 &&
                    lastUnpaidMonth?.length ===
                      (moment(
                      tpaPaymentsOverdue[
                        tpaPaymentsOverdue.length - 1
                      ].paymentDate,
                      ).isBefore(moment(), 'day')
                      ? tpaPaymentsOverdue?.length
                      : tpaPaymentsOverdue?.length - 1)
                    : true
                  }
                  isDownPayment={
                  moment(tpaInsMatrix.paymentDate).startOf('day').isSameOrAfter(moment().startOf('day'))                  
                  }
                  isOverDue={
                  lastUnpaidMonth?.length > 0 &&
                  lastUnpaidMonth[0]?.monthNumber !== 1 &&
                  tpaInsMatrix?.monthNumber ===
                    (moment(
                    tpaPaymentsOverdueCurrentYear[
                      tpaPaymentsOverdueCurrentYear?.length - 1
                    ]?.paymentDate,
                    ).isBefore(moment(), 'day')
                    ? tpaPaymentsOverdueCurrentYear[
                      tpaPaymentsOverdueCurrentYear.length - 1
                      ]?.monthNumber
                    : tpaPaymentsOverdueCurrentYear[
                      tpaPaymentsOverdueCurrentYear.length - 2
                      ]?.monthNumber)
                  }
                  isOverDueRollAmount={lastUnpaidMonth.reduce(
                  (sum: any, item: any) => sum + item.monthlyAmount,
                  0,
                  )}
                />
                </Grid>
              )}
            )}
          </Grid>
        </Grid>
        </Grid>
      </Grid>
      </PaperBox>
    </Fragment>
  );
};
export default PaymentPlanProgress;
